import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  InputAdornment,
  IconButton,
  Link as MuiLink,
  AppBar,
  Toolbar,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Public as PublicIcon,
} from "@mui/icons-material";
import { useNavigate, Link } from "react-router-dom";
import { apiRequest } from "../config/api";
import { Division, Player } from "../types";
import { useOrganization } from "../contexts/OrganizationContext";
import { useDivision } from "../contexts/DivisionContext";

function PlayerRegister() {
  const { currentOrg } = useOrganization();
  const { allDivisions } = useDivision();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [division, setDivision] = useState<Division>("Master");
  const [linkedPlayerId, setLinkedPlayerId] = useState("");
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const navigate = useNavigate();

  // Organization-aware colors
  const isPE = currentOrg?.slug === "padel-eagle";
  const primaryColor = isPE ? "#743444" : currentOrg?.branding?.primaryColor || "#1976d2";
  const secondaryColor = isPE ? "#E49421" : currentOrg?.branding?.secondaryColor || "#42a5f5";

  // Fetch available players when division changes
  useEffect(() => {
    fetchAvailablePlayers();
  }, [division]);

  const fetchAvailablePlayers = async () => {
    setLoadingPlayers(true);
    try {
      const response = await apiRequest(
        `/api/player/players/available?division=${division}`,
      );
      if (response.ok) {
        const players = await response.json();
        // Sort players alphabetically by name
        const sortedPlayers = players.sort((a: Player, b: Player) =>
          a.name.localeCompare(b.name),
        );
        setAvailablePlayers(sortedPlayers);
      }
    } catch (error) {
      console.error("Error fetching available players:", error);
    } finally {
      setLoadingPlayers(false);
    }
  };

  const validateForm = () => {
    if (
      !username ||
      !password ||
      !confirmPassword ||
      !firstName ||
      !lastName ||
      !email ||
      !linkedPlayerId
    ) {
      setError("All fields are required");
      return false;
    }

    if (!privacyAccepted) {
      setError("You must accept the Privacy Policy to register");
      return false;
    }

    if (username.length < 3) {
      setError("Username must be at least 3 characters");
      return false;
    }

    // Check for invalid characters in username
    const usernameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!usernameRegex.test(username)) {
      setError(
        "Username can only contain letters, numbers, hyphens, and underscores (no spaces)",
      );
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return false;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiRequest("/api/player/register", {
        method: "POST",
        body: JSON.stringify({
          username,
          password,
          firstName,
          lastName,
          email,
          division,
          linkedPlayerId,
        }),
      });

      if (response.ok) {
        setSuccess(true);
        setError("");
        // Clear form
        setUsername("");
        setPassword("");
        setConfirmPassword("");
        setFirstName("");
        setLastName("");
        setEmail("");
        setLinkedPlayerId("");
      } else {
        const data = await response.json();
        setError(data.error || "Registration failed");
      }
    } catch (error) {
      console.error("Error during registration:", error);
      setError("Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: "100%",
              textAlign: "center",
            }}
          >
            <Typography variant="h4" gutterBottom color="success.main">
              ✓ Registration Submitted!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your account registration request has been submitted successfully.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              An admin will review your request and approve your account
              shortly. You will be able to log in once your account is approved.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/player/login")}
            >
              Go to Login
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" elevation={2} sx={{ bgcolor: primaryColor }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Player Registration
          </Typography>
          <Button
            color="inherit"
            startIcon={<PublicIcon />}
            onClick={() => navigate("/")}
          >
            Home
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: "calc(100vh - 64px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
            <Typography variant="h4" gutterBottom align="center">
              Player Registration
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 3 }}
            >
              Create your player account to register for league days
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Username"
                value={username}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow letters, numbers, hyphens, and underscores
                  if (value === "" || /^[a-zA-Z0-9_-]+$/.test(value)) {
                    setUsername(value);
                  }
                }}
                margin="normal"
                required
                autoComplete="username"
                helperText="Letters, numbers, hyphens, and underscores only (no spaces)"
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="new-password"
                helperText="At least 6 characters"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                margin="normal"
                required
                autoComplete="given-name"
              />

              <TextField
                fullWidth
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                margin="normal"
                required
                autoComplete="family-name"
              />

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                autoComplete="email"
                helperText="Required for password reset and season registration confirmations"
              />

              <FormControl fullWidth margin="normal" required>
                <InputLabel>Division</InputLabel>
                <Select
                  value={division}
                  onChange={(e) => setDivision(e.target.value as Division)}
                  label="Division"
                >
                  {allDivisions
                    .filter((d) => d !== "Test" && d !== "All" && d !== "unassigned")
                    .map((div) => (
                    <MenuItem key={div} value={div}>
                      {div}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Select the division you currently play in
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth margin="normal" required>
                <InputLabel>Link to Player Profile</InputLabel>
                <Select
                  value={linkedPlayerId}
                  onChange={(e) => setLinkedPlayerId(e.target.value)}
                  label="Link to Player Profile"
                  disabled={loadingPlayers}
                >
                  {loadingPlayers ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                      &nbsp; Loading players...
                    </MenuItem>
                  ) : availablePlayers.length === 0 ? (
                    <MenuItem disabled>
                      No available player profiles in this division
                    </MenuItem>
                  ) : (
                    availablePlayers.map((player) => (
                      <MenuItem key={player.id} value={player.id}>
                        {player.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                <FormHelperText>
                  Select your existing player profile. This will be verified by
                  an admin.
                </FormHelperText>
              </FormControl>

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: "background.default",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I have read and accept the{" "}
                      <MuiLink
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontWeight: "bold" }}
                      >
                        Privacy Policy
                      </MuiLink>{" "}
                      and consent to the processing of my personal data. *
                    </Typography>
                  }
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  bgcolor: primaryColor,
                  "&:hover": { bgcolor: secondaryColor },
                }}
              >
                {isLoading ? <CircularProgress size={24} /> : "Register"}
              </Button>

              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Already have an account?{" "}
                  <MuiLink
                    component={Link}
                    to="/player/login"
                    underline="hover"
                  >
                    Login here
                  </MuiLink>
                </Typography>
              </Box>
            </form>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}

export default PlayerRegister;
